import { lazy } from 'react';

// Handles stale deploy hash mismatches: reloads the page once if a dynamic
// import chunk fails to fetch. Without this, the error persists across retries
// because the browser keeps requesting the same (now-missing) hash.
//
// We store the timestamp of the last auto-reload instead of a boolean flag.
// A boolean that is only cleared on a successful import disables recovery for
// the rest of the session if the reload never resolves the missing chunk (and
// a second deploy within the same tab would then surface the "Error loading
// page" boundary). A timestamp + cooldown re-arms automatically after the
// cooldown window, while still preventing a tight reload loop.
const RELOAD_TS_KEY = 'chunk_reload_at';
const RELOAD_COOLDOWN_MS = 10_000;

// Different engines word the failure differently (Chrome/V8, Firefox, Safari).
export function isChunkLoadError(err) {
  return /Loading chunk|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(
    err?.message ?? ''
  );
}

// Reloads the page at most once per cooldown window. Returns true if it fired.
export function reloadOnceForChunkError() {
  const last = Number(sessionStorage.getItem(RELOAD_TS_KEY) ?? 0);
  if (Date.now() - last > RELOAD_COOLDOWN_MS) {
    sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()));
    window.location.reload();
    return true;
  }
  return false;
}

export function lazyWithRetry(factory) {
  return lazy(() =>
    factory()
      .then((module) => {
        // Assets are in sync again — clear the marker so a future (post-deploy)
        // mismatch can auto-reload immediately instead of waiting out the cooldown.
        sessionStorage.removeItem(RELOAD_TS_KEY);
        return module;
      })
      .catch((err) => {
        if (isChunkLoadError(err) && reloadOnceForChunkError()) {
          return new Promise(() => {}); // suspend while the page reloads
        }
        throw err;
      })
  );
}
